import * as go from "gojs";

/**
 * A custom Tool for moving a label on a Link that keeps the label on the link's path.
 * This tool only works when the Link has a label marked by the "_isLinkLabel" property.
 */
class LinkLabelOnPathDraggingTool extends go.Tool {
  public label: go.GraphObject | null = null;
  private _originalIndex: number | null = null;
  private _originalFraction: number | null = null;

  constructor() {
    super();
    this.name = "LinkLabelOnPathDragging";
  }

  /**
   * This tool can only start if the mouse has moved enough so that it is not a click,
   * and if the mouse down point is on a GraphObject "label" in a Link Panel,
   * as determined by findLabel().
   */
  public canStart(): boolean {
    if (!super.canStart.call(this)) return false;
    const diagram = this.diagram;
    if (diagram === null) return false;
    // 只读模式下禁止拖动连线标签/连接点
    if (diagram.isReadOnly) return false;
    
    // require left button & that it has moved far enough away from the mouse down point, so it isn't a click
    const e = diagram.lastInput;
    if (!e.left) return false;
    if (!this.isBeyondDragSize()) return false;

    return this.findLabel() !== null;
  }

  /**
   * From the GraphObject at the mouse point, search up the visual tree until we get to
   * an object that has the "_isLinkLabel" property set to true and that is an immediate child of a Link Panel.
   * 另外支持挂在连线上的连接点节点（category 为 LinkJoint 的标签节点），使其可沿线拖动。
   * @return {go.GraphObject} This returns null if no such label is at the mouse down point.
   */
  public findLabel(): go.GraphObject | null {
    const diagram = this.diagram;
    if (!diagram) return null;

    const e = diagram.lastInput;
    let elt: go.GraphObject | null = diagram.findObjectAt(e.documentPoint, null, null);

    if (elt === null) return null;
    // 连线上的连接点小圆点（独立的标签节点）：可直接沿线拖动
    if (elt.part instanceof go.Node && elt.part.category === 'LinkJoint' && elt.part.labeledLink !== null) {
      return elt.part;
    }
    if (!(elt.part instanceof go.Link)) return null;
    while (elt.panel !== null && elt.panel !== elt.part) {
      elt = elt.panel;
    }
    // If it's not marked as "_isLinkLabel", don't consider it a label:
    if (!(elt as any)._isLinkLabel) return null;
    return elt;
  }

  /**
   * Start a transaction, call findLabel and remember it as the "label" property,
   * and remember the original values for the label's segment properties.
   */
  public doActivate(): void {
    this.startTransaction("Shifted Label");
    this.label = this.findLabel();
    if (this.label !== null) {
      this._originalIndex = this.label.segmentIndex;
      this._originalFraction = this.label.segmentFraction;
    }
    super.doActivate.call(this);
  }

  /**
   * Stop any ongoing transaction.
   */
  public doDeactivate(): void {
    super.doDeactivate.call(this);
    this.stopTransaction();
  }

  /**
   * Clear any reference to a label element.
   */
  public doStop(): void {
    this.label = null;
    super.doStop.call(this);
  }

  /**
   * Restore the label's original value for GraphObject.segment... properties.
   */
  public doCancel(): void {
    if (this.label !== null && this._originalIndex !== null && this._originalFraction !== null) {
      this.label.segmentIndex = this._originalIndex;
      this.label.segmentFraction = this._originalFraction;
    }
    super.doCancel.call(this);
  }

  /**
   * During the drag, call updateSegmentOffset in order to set the segment... properties of the label.
   */
  public doMouseMove(): void {
    if (!this.isActive) return;
    this.updateSegmentOffset();
  }

  /**
   * At the end of the drag, update the segment properties of the label and finish the tool,
   * completing a transaction.
   */
  public doMouseUp(): void {
    if (!this.isActive) return;
    this.updateSegmentOffset();
    this.transactionResult = "Shifted Label";
    this.stopTool();
  }

  /**
   * Save the label's GraphObject.segmentIndex and segmentFraction at the closest point to the mouse.
   */
  public updateSegmentOffset(): void {
    const lab = this.label;
    if (lab === null) return;
    // 标签可能是 Link 面板内的元素，也可能是挂在连线上的连接点节点
    const link =
      lab.part instanceof go.Link ? lab.part : lab instanceof go.Node ? lab.labeledLink : null;
    if (!(link instanceof go.Link)) return;

    const last = this.diagram!.lastInput.documentPoint;
    let idx = link.findClosestSegment(last);
    idx = Math.min(Math.max(link.firstPickIndex, idx), link.lastPickIndex - 1);
    const p1 = link.getPoint(idx);
    const p2 = link.getPoint(idx + 1);
    const total = Math.sqrt(p1.distanceSquaredPoint(p2));
    const p = last.copy().projectOntoLineSegmentPoint(p1, p2);
    const frac = Math.sqrt(p1.distanceSquaredPoint(p)) / total;
    lab.segmentIndex = idx;
    lab.segmentFraction = frac;
  }
}

// 标准TypeScript继承已在class声明中完成

export default LinkLabelOnPathDraggingTool;